import type {
  StoreCatalogResponse,
  StoreOrderState,
  StoreOrdersResponse,
  StoreOrderWire,
  StoreRecreditPreviewResponse,
} from '@kenocyber/shared';
import {
  ALPHA_DISCOUNT_DETAIL,
  ALPHA_DISCOUNT_HEADLINE,
  ALPHA_NOT_150,
  ALPHA_PROMOTION_UNDATED,
  ALPHA_RECREDIT_DETAIL,
  ALPHA_RECREDIT_HEADLINE,
  ALPHA_TERMS_LINK_LABEL,
  ALPHA_TERMS_TITLE,
  formatBrl,
  RECREDIT_EXCLUSION_LABELS,
  type RecreditExclusionReason,
  VIP_GOLD_BONUS_PERCENT,
  VIP_XP_BONUS_PERCENT,
} from '@kenocyber/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createStoreOrder,
  fetchStoreCatalog,
  fetchStoreOrder,
  fetchStoreOrders,
  fetchStoreRecreditPreview,
  HuntApiError,
  newIdempotencyKey,
} from '../api/hunt';
import {
  benefitLabel,
  countdownLabel,
  discountLabel,
  durationLabel,
  instantLabel,
  invalidatesConfirmation,
  isFinishedOrder,
  isLiveOrder,
  type PromotionClock,
  phaseLabel,
  pollDelayMs,
  presentOrder,
  promotionClockFrom,
  promotionCountdownLabel,
  promotionRemainingMs,
  shouldPoll,
  storeErrorMessage,
} from './store-presentation';

/**
 * The shop, as a floating panel over the arena.
 *
 * ## Nothing here decides anything
 *
 * Every number on this screen came from `GET /api/store/catalog`, from
 * `GET /api/store/recredit-preview` or from an order the server created: the
 * prices, both of them, the discount, the phase, the Cash amounts, the VIP
 * durations, the remaining allowance, the debt outcome, the expiry, the 30%
 * bonus. There is no second price table in the frontend and no arithmetic on
 * money - the wire carries `priceLabel` beside `priceCents` precisely so the
 * label a player reads is derived from the integer the charge uses.
 *
 * The promotion is the sharpest case. Whether half price applies is decided by
 * the SERVER's clock and travels as `promotion.active`; the countdown below is
 * presentation over an anchored server instant, and it cannot make anything
 * cheaper or more expensive. A browser told it is next year sees a countdown at
 * zero and still pays whatever the catalogue says.
 *
 * ## Every word about the reset comes from `alpha-terms.ts`
 *
 * The headline, the list of what is erased, the twelve rules, the checkbox and
 * the sentence refusing to add 50% to 30% are all shared constants, and the
 * version of them is what the order records having been accepted. A component
 * that composed its own sentence would eventually promise something no order
 * says was promised.
 *
 * ## The browser never talks to Mercado Pago
 *
 * It polls OUR order route. A client that could ask the provider directly could
 * be lied to by anything between it and the provider, and would need a
 * credential to ask with.
 *
 * ## The idempotency key belongs to the ATTEMPT
 *
 * Generated once when the player opens a confirmation, kept across every retry
 * of that attempt, and thrown away when the attempt is abandoned - including
 * when the server refuses it as `price_changed` or `terms_outdated`, because
 * what happens next is a NEW decision about a different offer.
 *
 * ## No canvas, no Three.js
 *
 * The QR arrives from the server as a base64 image and is drawn as an `<img>`.
 * The product emblems are inline SVG - no raster asset, no manifest, alpha
 * preserved by construction. Nothing in this panel paints pixels itself.
 */

/** The stage's wording, exactly as the catalogue sent it. */
type StoreNotice = StoreCatalogResponse['notice'];
/** One reset's projection, exactly as the preview sent it. */
type StoreProjection = StoreRecreditPreviewResponse['projections'][number];

type TabKey = 'cash' | 'vip' | 'orders' | 'recredit';

const TABS: readonly { key: TabKey; label: string }[] = [
  { key: 'cash', label: 'CASH' },
  { key: 'vip', label: 'VIP' },
  { key: 'recredit', label: 'RECREDITO' },
  { key: 'orders', label: 'MEUS PEDIDOS' },
];

/** The order filters, in a fixed order so the row never reflows. */
const FILTERS: readonly { key: string; label: string; states: readonly StoreOrderState[] }[] = [
  { key: 'all', label: 'Todos', states: [] },
  { key: 'pending', label: 'Pendentes', states: ['created', 'pending', 'approved'] },
  { key: 'done', label: 'Concluídos', states: ['granted'] },
  { key: 'closed', label: 'Encerrados', states: ['rejected', 'cancelled', 'expired'] },
  { key: 'review', label: 'Em análise', states: ['manual_review'] },
  { key: 'refunded', label: 'Reembolsados', states: ['refund_pending', 'refunded'] },
];

/**
 * A monotonic reading, in milliseconds.
 *
 * `performance.now()` counts from the document's origin and is UNAFFECTED by
 * the system clock moving. That is the whole reason the countdown uses it:
 * changing the Windows clock cannot reopen a promotion, because nothing here
 * ever reads the wall clock again after the catalogue answers.
 */
function monotonicNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

interface StorePanelProps {
  readonly onClose: () => void;
  /** Told after a purchase lands, so the HUD re-reads the wallet and VIP. */
  readonly onSettled: () => void;
}

export function StorePanel({ onClose, onSettled }: StorePanelProps) {
  const [tab, setTab] = useState<TabKey>('cash');
  const [catalog, setCatalog] = useState<StoreCatalogResponse | null>(null);
  const [orders, setOrders] = useState<StoreOrdersResponse | null>(null);
  const [recredit, setRecredit] = useState<StoreRecreditPreviewResponse | null>(null);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  /** The product awaiting a confirmation click. Nothing is charged until then. */
  const [confirming, setConfirming] = useState<string | null>(null);
  /**
   * WHAT was accepted, not merely THAT something was.
   *
   * The value is a fingerprint of the offer on screen - product, effective
   * price, phase and terms version. The box is ticked only while it equals the
   * current offer, so a price change, the promotion ending or a new wording
   * unticks it by construction rather than by an effect somebody has to
   * remember to write. There is no path that reuses an acceptance of a
   * different offer, because there is nowhere to keep one.
   */
  const [acceptedOffer, setAcceptedOffer] = useState<string | null>(null);
  /** The order being watched. The Pix screen, and the polling, hang off this. */
  const [active, setActive] = useState<StoreOrderWire | null>(null);
  const [copied, setCopied] = useState(false);
  /** Re-rendered once a second, only while a countdown is on screen. */
  const [nowMs, setNowMs] = useState(() => Date.now());

  /** The promotion's anchor. Re-taken on every catalogue read. */
  const [clock, setClock] = useState<PromotionClock | null>(null);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  /**
   * The key for the CURRENT attempt.
   *
   * A ref, not state: a re-render must not change it, and a retry of the same
   * attempt must reuse it so a lost response returns the order it created
   * instead of opening a second charge.
   */
  const attemptKey = useRef<string | null>(null);
  /**
   * True from the instant a confirmation is clicked.
   *
   * A REF, because `busy` is React state and does not change until the next
   * render: the second click of a double click reads the old `false` and
   * creates a second order. This one changes synchronously.
   */
  const inFlight = useRef(false);
  /**
   * The settle callback, held by reference.
   *
   * `onSettled` is an inline arrow in `App`, so its identity changes on every
   * render - and the arena re-renders the app several times a second. Naming it
   * in the polling effect's dependencies therefore tore the effect down and
   * rebuilt it faster than its own four-second timeout could ever fire, and the
   * screen sat on AGUARDANDO PAGAMENTO for ever while the server had already
   * delivered. Found by the browser journey; the unit tests pass a stable
   * callback and could not have seen it.
   */
  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;
  /** Guards the one re-read a countdown reaching zero is allowed to trigger. */
  const expiryHandled = useRef(false);

  const report = useCallback((unknownError: unknown) => {
    const apiError = unknownError instanceof HuntApiError ? unknownError : null;
    // Never the server's own string: a message can carry an internal reason.
    setError(storeErrorMessage(apiError?.reason, apiError?.status));
  }, []);

  const refreshCatalog = useCallback(async () => {
    try {
      const answer = await fetchStoreCatalog();
      setCatalog(answer);
      /*
       * The anchor is taken HERE, beside the answer it belongs to. Taking it
       * later would count the round trip as elapsed promotion time.
       */
      setClock(
        promotionClockFrom(answer.promotion.serverNow, answer.promotion.endsAt, monotonicNow()),
      );
      expiryHandled.current = false;
    } catch (unknownError) {
      report(unknownError);
    }
  }, [report]);

  const refreshOrders = useCallback(async () => {
    try {
      setOrders(await fetchStoreOrders());
    } catch (unknownError) {
      report(unknownError);
    }
  }, [report]);

  const refreshRecredit = useCallback(async () => {
    try {
      setRecredit(await fetchStoreRecreditPreview());
    } catch (unknownError) {
      report(unknownError);
    }
  }, [report]);

  useEffect(() => {
    void refreshCatalog();
    void refreshOrders();
    void refreshRecredit();
  }, [refreshCatalog, refreshOrders, refreshRecredit]);

  /**
   * A charge that was still open when the panel last closed.
   *
   * Recovered from the SERVER, never from local storage: the order is the
   * server's, the reconciler kept working while nobody was looking, and a
   * payment made with the shop closed has already been delivered by the time
   * this runs.
   */
  useEffect(() => {
    if (active || !orders) {
      return;
    }
    const live = orders.orders.find((order) => isLiveOrder(order.state));
    if (live) {
      setActive(live);
    }
  }, [orders, active]);

  /* ------------------------------------------------------------------ */
  /*  The promotional countdown                                         */
  /* ------------------------------------------------------------------ */

  /**
   * One tick a second, from the anchored server instant.
   *
   * Reaching zero does NOT switch the price locally: it re-reads the catalogue,
   * and the server decides. Anything else would let a browser end - or extend -
   * a promotion by itself, and the whole point is that it cannot.
   */
  useEffect(() => {
    if (!clock) {
      setRemainingMs(null);
      return;
    }
    const update = () => {
      const left = promotionRemainingMs(clock, monotonicNow());
      setRemainingMs(left);
      if (left <= 0 && !expiryHandled.current) {
        expiryHandled.current = true;
        // The offer on screen is stale, so an acceptance of it is too.
        setAcceptedOffer(null);
        void refreshCatalog();
      }
    };
    update();
    const timer = setInterval(update, 1_000);
    return () => clearInterval(timer);
  }, [clock, refreshCatalog]);

  /**
   * Coming back to the tab re-reads the catalogue.
   *
   * A background tab's timers are throttled unpredictably and a laptop that was
   * asleep for two hours has a countdown two hours wrong. Re-anchoring on
   * return is cheaper than trusting either, and it is the same request the
   * panel already makes on open.
   */
  useEffect(() => {
    const resync = () => {
      if (!document.hidden) {
        void refreshCatalog();
      }
    };
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('focus', resync);
    return () => {
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('focus', resync);
    };
  }, [refreshCatalog]);

  /* ------------------------------------------------------------------ */
  /*  Polling                                                           */
  /* ------------------------------------------------------------------ */

  /**
   * Watches one order until it stops changing.
   *
   * Stops on a terminal state, stops when the panel unmounts, and pauses while
   * the tab is hidden - `visibilitychange` re-runs the effect, so coming back
   * resumes immediately rather than waiting out an interval.
   *
   * The timeout is chained rather than an interval: a slow response must not
   * queue a second request behind it.
   */
  useEffect(() => {
    const orderId = active?.orderId;
    const state = active?.state;
    if (!orderId || !state || !isLiveOrder(state)) {
      return;
    }
    let cancelled = false;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelled || !shouldPoll(state, document.hidden)) {
        return;
      }
      try {
        const answer = await fetchStoreOrder(orderId);
        if (cancelled) {
          return;
        }
        failures = 0;
        setActive(answer.order);
        setCatalog((current) => (current ? { ...current, limit: answer.limit } : current));
        if (isFinishedOrder(answer.order.state) || answer.order.state === 'granted') {
          // The wallet and the VIP window moved on the server. Re-read them
          // rather than predicting: the debt settlement makes "how much landed"
          // something only the server can answer.
          settledRef.current();
          void refreshOrders();
          void refreshCatalog();
          // A delivered purchase changes the estimate, and a refunded one
          // changes it the other way.
          void refreshRecredit();
          return;
        }
      } catch {
        // A failed poll is not shown: the order is fine, the network was not.
        failures += 1;
      }
      if (!cancelled) {
        timer = setTimeout(() => void tick(), pollDelayMs(failures));
      }
    };

    timer = setTimeout(() => void tick(), pollDelayMs(0));
    const onVisibility = () => {
      if (!document.hidden && !cancelled) {
        void tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // Deliberately NOT `onSettled`: see `settledRef`. The refresh callbacks are
    // `useCallback`s over a stable reporter, so they are stable.
  }, [active?.orderId, active?.state, refreshOrders, refreshCatalog, refreshRecredit]);

  /** One tick a second, and only while a Pix charge is counting down. */
  useEffect(() => {
    if (!active || !isLiveOrder(active.state) || !active.expiresAt) {
      return;
    }
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      // The rules sit ON TOP of the shop, so Escape dismisses them first. A key
      // that closed both at once would lose the player's place.
      if (rulesOpen) {
        setRulesOpen(false);
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, rulesOpen]);

  /* ------------------------------------------------------------------ */
  /*  Buying                                                            */
  /* ------------------------------------------------------------------ */

  const promotion = catalog?.promotion ?? null;
  /**
   * The stage's own wording, from the SERVER.
   *
   * The three stages promise different things - one reset, one reset, or none -
   * so the headline, the rules and the sentence on the checkbox all travel with
   * the catalogue rather than being chosen here. A panel that picked its own
   * would eventually warn about a reset the order never recorded agreeing to.
   */
  const notice = catalog?.notice ?? null;
  const product = useMemo(
    () => catalog?.products.find((entry) => entry.productId === confirming) ?? null,
    [catalog, confirming],
  );

  /**
   * The offer, as one string.
   *
   * Product, the price that would actually be charged, the commercial phase and
   * the wording. Any of the four changing makes a previous acceptance stop
   * matching, which is exactly what unticks the box.
   */
  const offerFingerprint = useMemo(() => {
    if (!product || !catalog) {
      return null;
    }
    return [
      product.productId,
      product.priceCents,
      // The STAGE, because the three of them promise different things: an
      // acceptance given in the closed alpha must not authorise a purchase
      // made after the reset it was warning about.
      catalog.stage,
      catalog.promotion.active ? 'promo' : 'normal',
      catalog.termsVersion,
    ].join('|');
  }, [product, catalog]);

  const accepted = offerFingerprint !== null && acceptedOffer === offerFingerprint;

  const beginAttempt = (productId: string) => {
    // A NEW attempt gets a new key. Abandoning the previous one is what makes
    // it new; nothing else here ever generates one.
    attemptKey.current = newIdempotencyKey('store');
    setError(null);
    setAcceptedOffer(null);
    setConfirming(productId);
  };

  const abandonAttempt = () => {
    attemptKey.current = null;
    setAcceptedOffer(null);
    setConfirming(null);
  };

  const confirm = async () => {
    // The guard a double click hits, and it has to be synchronous.
    if (!product || !catalog || inFlight.current) {
      return;
    }
    /*
     * The box is the gate, and it is checked HERE as well as on the button.
     *
     * A disabled button is a presentation fact; this is the one that decides
     * whether a request leaves. Nothing that is not accepted can be charged,
     * whatever the DOM says.
     */
    if (!accepted) {
      return;
    }
    const key = attemptKey.current;
    if (!key) {
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const created = await createStoreOrder({
        productId: product.productId,
        idempotencyKey: key,
        // Both are CLAIMS the server checks. Sending the price it displayed is
        // what turns a stale screen into a refusal instead of a surprise.
        expectedPriceCents: product.priceCents,
        acceptedTermsVersion: catalog.termsVersion,
      });
      setActive(created.order);
      setCatalog((current) => (current ? { ...current, limit: created.limit } : current));
      setConfirming(null);
      setAcceptedOffer(null);
      void refreshOrders();
    } catch (unknownError) {
      const reason = unknownError instanceof HuntApiError ? unknownError.reason : null;
      if (invalidatesConfirmation(reason)) {
        /*
         * What the player looked at is not what would be charged or agreed to.
         *
         * The confirmation closes, the acceptance is dropped, the key is thrown
         * away and the catalogue is re-read. There is deliberately NO retry:
         * repeating with the same key after a price change would buy at a price
         * nobody looked at, which is the exact failure the check prevents.
         */
        attemptKey.current = null;
        setConfirming(null);
        setAcceptedOffer(null);
        void refreshCatalog();
      }
      /*
       * Otherwise the key is KEPT. The player may press again, and repeating
       * with the same key returns the order a lost response already created
       * rather than opening a second charge.
       */
      report(unknownError);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const copyPix = async () => {
    const code = active?.pix?.qrCode;
    if (!code) {
      return;
    }
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Some browsers refuse the clipboard without a gesture they recognise.
      // The code is on screen and selectable either way.
      setError('Não foi possível copiar. Selecione o código e copie manualmente.');
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Derived                                                           */
  /* ------------------------------------------------------------------ */

  const limit = catalog?.limit ?? null;
  const purchasing = catalog?.purchasingEnabled ?? false;
  const debt = BigInt(catalog?.cashDebt ?? '0');
  const products = useMemo(
    () =>
      (catalog?.products ?? []).filter((entry) => entry.kind === (tab === 'vip' ? 'vip' : 'cash')),
    [catalog, tab],
  );
  const visibleOrders = useMemo(() => {
    const states = FILTERS.find((entry) => entry.key === filter)?.states ?? [];
    const rows = orders?.orders ?? [];
    return states.length === 0 ? rows : rows.filter((order) => states.includes(order.state));
  }, [orders, filter]);

  /** Why a purchase button is disabled, or null when it is not. */
  const blockedReason = (priceCents: number): string | null => {
    if (!purchasing) {
      return 'PAGAMENTOS EM CONFIGURAÇÃO';
    }
    if (!limit) {
      return null;
    }
    if (limit.pendingOrders >= limit.maxPendingOrders) {
      return 'LIMITE DE PEDIDOS PENDENTES';
    }
    if (priceCents > limit.remainingCents) {
      return 'LIMITE DE 24H ATINGIDO';
    }
    return null;
  };

  const showingCatalogue = !active && !product;

  return (
    <aside className="store-panel" data-testid="store-panel" aria-label="Loja">
      <header className="market-panel-header">
        <h2>Loja</h2>
        <button
          type="button"
          className="overview-close"
          data-testid="store-close"
          aria-label="Fechar loja"
          onClick={onClose}
        >
          ×
        </button>
      </header>

      {/* Every status change is announced, not only painted. */}
      <p className="store-live" role="status" aria-live="polite" data-testid="store-live">
        {active ? presentOrder(active.state).label : ''}
      </p>

      {error ? (
        <p className="trade-error" role="alert" data-testid="store-error">
          {error}
        </p>
      ) : null}

      {notice ? <AlphaNotice notice={notice} onOpenRules={() => setRulesOpen(true)} /> : null}

      {!purchasing ? (
        <p className="store-disabled-banner" data-testid="store-disabled">
          <span className="store-badge">PAGAMENTOS EM CONFIGURAÇÃO</span>A compra será liberada
          assim que o pagamento estiver configurado.
        </p>
      ) : null}

      {notice ? (
        <p className="store-changes" data-testid="store-changes">
          {notice.changesNotice}
        </p>
      ) : null}

      {debt > 0n ? (
        <p className="store-debt" data-testid="store-debt">
          <span className="store-badge is-warning">DÍVIDA DE CASH</span>
          Você tem <strong>{debt.toString(10)} Cash</strong> em aberto de um reembolso. Comprar Cash
          quita essa dívida primeiro; só o restante fica disponível para gastar. Comprar VIP
          continua liberado e não quita a dívida.
        </p>
      ) : null}

      <div className="trade-tabs" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            className={tab === entry.key ? 'is-active' : ''}
            data-testid={`store-tab-${entry.key}`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {limit ? (
        <div className="store-limit" data-testid="store-limit">
          <span>
            Disponível nas próximas 24h: <strong>{formatBrl(limit.remainingCents)}</strong> de{' '}
            {formatBrl(limit.limitCents)}
          </span>
          <span>
            Pedidos pendentes: <strong>{limit.pendingOrders}</strong> de {limit.maxPendingOrders}
          </span>
          {limit.remainingCents === 0 && limit.resetsAt ? (
            <span className="store-limit-reset">
              Libera parte do limite em {instantLabel(limit.resetsAt)}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* --- The Pix screen replaces everything else while it is up --- */}
      {active ? (
        <PaymentScreen
          order={active}
          nowMs={nowMs}
          copied={copied}
          onCopy={() => void copyPix()}
          onDismiss={() => {
            setActive(null);
            void refreshCatalog();
            void refreshOrders();
            void refreshRecredit();
          }}
          onRetry={() => {
            setActive(null);
            beginAttempt(active.product.productId);
          }}
        />
      ) : null}

      {/* --- Confirmation, inside the panel --- */}
      {!active && product ? (
        <ConfirmPurchase
          product={product}
          notice={notice}
          promotionActive={promotion?.active ?? false}
          discountBasisPoints={promotion?.discountBasisPoints ?? 0}
          termsVersion={catalog?.termsVersion ?? ''}
          accepted={accepted}
          busy={busy}
          onAcceptChange={(next) => setAcceptedOffer(next ? offerFingerprint : null)}
          onOpenRules={() => setRulesOpen(true)}
          onConfirm={() => void confirm()}
          onCancel={abandonAttempt}
        />
      ) : null}

      {/* --- Products --- */}
      {showingCatalogue && (tab === 'cash' || tab === 'vip') ? (
        <section className={`market-body store-body store-${tab}`} data-testid={`store-${tab}`}>
          <PromotionBanner
            promotion={promotion}
            remainingMs={remainingMs}
            onOpenRules={() => setRulesOpen(true)}
          />
          {tab === 'vip' ? <VipSummary catalog={catalog} /> : null}
          <ul className="store-cards">
            {products.map((entry) => {
              const blocked = blockedReason(entry.priceCents);
              const promotional = (promotion?.active ?? false) && entry.promoPriceCents !== null;
              return (
                <li
                  key={entry.productId}
                  className={`store-card store-card-${entry.kind}`}
                  data-testid={`store-product-${entry.productId}`}
                >
                  <span className="store-card-glow" aria-hidden="true" />
                  {promotional ? (
                    <span className="store-card-off" data-testid={`store-off-${entry.productId}`}>
                      {discountLabel(promotion?.discountBasisPoints)}
                    </span>
                  ) : null}
                  <span className="store-card-kind">{entry.kind === 'cash' ? 'CASH' : 'VIP'}</span>
                  <ProductEmblem kind={entry.kind} />
                  {/*
                    The server's own name, and only it. Rendering the raw
                    `cashAmount` beside it printed the ungrouped digits under
                    the grouped ones - the same number formatted twice, once by
                    the frontend, which is exactly what this panel must not do.
                  */}
                  <span className="store-card-benefit">{entry.name}</span>
                  <span className="store-card-name">{entry.description}</span>
                  <span className="store-card-prices">
                    {promotional ? (
                      <s
                        className="store-card-was"
                        data-testid={`store-normal-price-${entry.productId}`}
                      >
                        {entry.normalPriceLabel}
                      </s>
                    ) : null}
                    <span
                      className={`store-card-price${promotional ? ' is-promo' : ''}`}
                      data-testid={`store-price-${entry.productId}`}
                    >
                      {entry.priceLabel}
                    </span>
                  </span>
                  {entry.debtPreview && BigInt(entry.debtPreview.debtSettled) > 0n ? (
                    <span
                      className="store-card-debt"
                      data-testid={`store-card-debt-${entry.productId}`}
                    >
                      Quita {entry.debtPreview.debtSettled} · libera{' '}
                      {entry.debtPreview.cashCredited} · restam {entry.debtPreview.debtRemaining}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="neon-button store-buy"
                    data-testid={`store-buy-${entry.productId}`}
                    disabled={blocked !== null}
                    onClick={() => beginAttempt(entry.productId)}
                  >
                    {blocked ?? 'COMPRAR COM PIX'}
                  </button>
                  {entry.kind === 'cash' ? (
                    <span className="store-card-note">
                      Elegível ao recredito de 30% no lançamento
                    </span>
                  ) : (
                    <span className="store-card-note">Não gera recredito de Cash</span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {/* --- The launch estimate --- */}
      {showingCatalogue && tab === 'recredit' ? (
        <RecreditSection preview={recredit} onOpenRules={() => setRulesOpen(true)} />
      ) : null}

      {/* --- History --- */}
      {showingCatalogue && tab === 'orders' ? (
        <section className="market-body store-body" data-testid="store-orders">
          {/* A real fieldset: the filters ARE a group of related controls, and
              a div wearing role="group" is the same thing spelled worse. */}
          <fieldset className="store-filters">
            <legend className="store-live">Filtrar pedidos</legend>
            {FILTERS.map((entry) => (
              <button
                key={entry.key}
                type="button"
                aria-pressed={filter === entry.key}
                className={filter === entry.key ? 'is-active' : ''}
                data-testid={`store-filter-${entry.key}`}
                onClick={() => setFilter(entry.key)}
              >
                {entry.label}
              </button>
            ))}
          </fieldset>
          <ul className="market-rows store-history">
            {visibleOrders.length === 0 ? (
              <li className="trade-empty">Nenhum pedido aqui.</li>
            ) : null}
            {visibleOrders.map((order) => (
              <HistoryRow
                key={order.orderId}
                order={order}
                purchasing={purchasing}
                onResume={() => setActive(order)}
                onRenew={() => {
                  setTab(order.product.kind === 'vip' ? 'vip' : 'cash');
                  beginAttempt(order.product.productId);
                }}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {rulesOpen && notice ? (
        <AlphaRules notice={notice} onClose={() => setRulesOpen(false)} />
      ) : null}
    </aside>
  );
}

/* -------------------------------------------------------------------------- */
/*  The alpha notice and the full rules                                       */
/* -------------------------------------------------------------------------- */

/**
 * The banner nobody can miss, and it is deliberately at the top.
 *
 * Loud, but bounded: it is a few lines with a button, not a wall of text over
 * the products. A warning that buried the catalogue would be scrolled past,
 * which is the same as not showing it.
 */
function AlphaNotice({ notice, onOpenRules }: { notice: StoreNotice; onOpenRules: () => void }) {
  return (
    <section
      className={notice.resetsAtEnd ? 'store-alpha-notice' : 'store-alpha-notice is-permanent'}
      data-testid="store-alpha-notice"
    >
      <strong className="store-alpha-headline">{notice.headline}</strong>
      <p>{notice.summary}</p>
      <button
        type="button"
        className="store-rules-link"
        data-testid="store-open-rules"
        onClick={onOpenRules}
      >
        {ALPHA_TERMS_LINK_LABEL}
      </button>
    </section>
  );
}

/**
 * Everything, in the shared wording, over the shop.
 *
 * The two benefits are shown side by side with the sentence that refuses to add
 * them up: half price is what is paid TODAY, the 30% is what returns at LAUNCH,
 * and "150%" is not something this store has ever offered.
 */
function AlphaRules({ notice, onClose }: { notice: StoreNotice; onClose: () => void }) {
  return (
    <div
      className="store-rules-overlay"
      data-testid="store-alpha-rules"
      role="dialog"
      aria-modal="true"
      aria-label={ALPHA_TERMS_TITLE}
    >
      <div className="store-rules-sheet">
        <header className="market-panel-header">
          <h3>{ALPHA_TERMS_TITLE}</h3>
          <button
            type="button"
            className="overview-close"
            data-testid="store-rules-close"
            aria-label="Fechar regras"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="store-rules-body">
          <strong className="store-alpha-headline">{notice.headline}</strong>

          {/* The two benefits, side by side, and never added together. Shown
              only while there is a reset to be compensated for. */}
          {notice.resetsAtEnd ? (
            <>
              <div className="store-benefit-split" data-testid="store-benefit-split">
                <div className="store-benefit-card is-discount">
                  <strong>{ALPHA_DISCOUNT_HEADLINE}</strong>
                  <span>{ALPHA_DISCOUNT_DETAIL}</span>
                </div>
                <div className="store-benefit-card is-recredit">
                  <strong>{ALPHA_RECREDIT_HEADLINE}</strong>
                  <span>{ALPHA_RECREDIT_DETAIL}</span>
                </div>
              </div>
              <p className="store-rules-warning" data-testid="store-not-150">
                {ALPHA_NOT_150}
              </p>
            </>
          ) : null}

          {notice.wiped.length > 0 ? (
            <>
              <h4>O que será apagado</h4>
              <ul className="store-wiped" data-testid="store-wiped">
                {notice.wiped.map((entry) => (
                  <li key={entry}>{entry}</li>
                ))}
              </ul>
            </>
          ) : null}

          <h4>Regras desta fase</h4>
          <ol className="store-rules-list" data-testid="store-stage-rules">
            {notice.paragraphs.map((paragraph) => (
              <li key={paragraph}>{paragraph}</li>
            ))}
          </ol>

          <h4>O que conta, e o que não conta</h4>
          <ol className="store-rules-list" data-testid="store-common-rules">
            {notice.commonRules.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ol>

          <p className="trade-note" data-testid="store-vip-note">
            {notice.vipNote}
          </p>
          <p className="trade-note" data-testid="store-changes-notice">
            {notice.changesNotice}
          </p>
        </div>
        <div className="market-confirm-actions">
          <button type="button" className="neon-button" onClick={onClose}>
            ENTENDI
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  The promotion                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The banner, in three shapes, and it never invents one.
 *
 * Running: the badge, the deadline the server sent, and a countdown towards it.
 * Finished: the normal price and a plain statement that the promotion ended -
 * no badge, no struck price, no countdown. Undated: one discreet line saying so,
 * because a missing date is a decision nobody has taken yet and a countdown
 * towards nothing would be a deadline this screen made up.
 */
function PromotionBanner({
  promotion,
  remainingMs,
  onOpenRules,
}: {
  promotion: StoreCatalogResponse['promotion'] | null;
  remainingMs: number | null;
  onOpenRules: () => void;
}) {
  if (!promotion) {
    return null;
  }
  if (!promotion.active) {
    return (
      <p className="store-promo-quiet" data-testid="store-promo-quiet">
        {promotion.endsAt === null
          ? ALPHA_PROMOTION_UNDATED
          : `A promoção do Alpha terminou em ${instantLabel(promotion.endsAt) ?? '—'}. Os preços abaixo são os normais.`}
      </p>
    );
  }
  const off = discountLabel(promotion.discountBasisPoints);
  return (
    <section className="store-promo-banner" data-testid="store-promo-banner">
      <span className="store-promo-badge" data-testid="store-promo-badge">
        {off}
      </span>
      <div className="store-promo-text">
        <strong>Alpha fechado termina em</strong>
        <span className="store-promo-countdown" data-testid="store-promo-countdown">
          {promotionCountdownLabel(remainingMs ?? 0)}
        </span>
        <span className="store-promo-until" data-testid="store-promo-until">
          Por tempo limitado até {instantLabel(promotion.endsAt) ?? '—'} — horário de Brasília. O
          preço promocional vale apenas até lá.
        </span>
      </div>
      <button type="button" className="store-rules-link" onClick={onOpenRules}>
        {ALPHA_TERMS_LINK_LABEL}
      </button>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  The confirmation                                                          */
/* -------------------------------------------------------------------------- */

/**
 * What is being bought, under which rules, and the box that gates it.
 *
 * The submit button starts DISABLED and stays disabled until the box is ticked
 * for THIS offer. Ticking is not a preference that survives a price change: the
 * panel stores which offer was accepted, so anything moving unticks it.
 */
function ConfirmPurchase({
  product,
  notice,
  promotionActive,
  discountBasisPoints,
  termsVersion,
  accepted,
  busy,
  onAcceptChange,
  onOpenRules,
  onConfirm,
  onCancel,
}: {
  product: StoreCatalogResponse['products'][number];
  notice: StoreNotice | null;
  promotionActive: boolean;
  discountBasisPoints: number;
  termsVersion: string;
  accepted: boolean;
  busy: boolean;
  onAcceptChange: (next: boolean) => void;
  onOpenRules: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const promotional = promotionActive && product.promoPriceCents !== null;
  return (
    <div className="market-confirm" data-testid="store-confirm" role="dialog" aria-modal="true">
      <h3>Confirmar compra</h3>
      <div className="store-confirm-product">
        <ProductEmblem kind={product.kind} />
        <span data-testid="store-confirm-product">{product.name}</span>
      </div>
      <dl className="store-confirm-lines">
        <div>
          <dt>Você recebe</dt>
          <dd data-testid="store-confirm-benefit">
            {benefitLabel({
              kind: product.kind,
              cashAmount: product.cashAmount,
              vipSeconds: product.vipSeconds,
            })}
          </dd>
        </div>
        <div>
          <dt>Valor</dt>
          <dd data-testid="store-confirm-price">
            {promotional ? (
              <s className="store-card-was" data-testid="store-confirm-normal-price">
                {product.normalPriceLabel}
              </s>
            ) : null}
            <strong>{product.priceLabel}</strong>
            {promotional ? (
              <span className="store-badge" data-testid="store-confirm-off">
                {discountLabel(discountBasisPoints)}
              </span>
            ) : null}
          </dd>
        </div>
        <div>
          <dt>Método</dt>
          <dd>Pix</dd>
        </div>
      </dl>
      {product.debtPreview && BigInt(product.debtPreview.debtSettled) > 0n ? (
        <p className="store-debt-preview" data-testid="store-confirm-debt">
          Quita {product.debtPreview.debtSettled} Cash de dívida ·{' '}
          <strong>{product.debtPreview.cashCredited} Cash</strong> ficam disponíveis · restam{' '}
          {product.debtPreview.debtRemaining} de dívida
        </p>
      ) : null}

      {notice ? (
        <p
          className={
            notice.resetsAtEnd ? 'store-confirm-reset' : 'store-confirm-reset is-permanent'
          }
          data-testid="store-confirm-reset"
        >
          <span className={notice.resetsAtEnd ? 'store-badge is-warning' : 'store-badge'}>
            {notice.resetsAtEnd ? 'RESET NO FIM DESTA FASE' : 'ECONOMIA PERMANENTE'}
          </span>
          {notice.summary}
        </p>
      ) : null}
      <button
        type="button"
        className="store-rules-link"
        data-testid="store-confirm-rules"
        onClick={onOpenRules}
      >
        {ALPHA_TERMS_LINK_LABEL}
      </button>

      <label className="store-accept" data-testid="store-accept-label">
        <input
          type="checkbox"
          data-testid="store-accept"
          checked={accepted}
          onChange={(event) => onAcceptChange(event.target.checked)}
        />
        {/* The sentence of THIS stage. Ticking one stage's wording must never
            authorise a purchase made under another's. */}
        <span>{notice?.checkbox ?? ''}</span>
      </label>
      <p className="trade-note">
        O benefício é entregue automaticamente após a confirmação do pagamento. Versão das regras:{' '}
        <span data-testid="store-confirm-terms">{termsVersion}</span>.
      </p>

      <div className="market-confirm-actions">
        <button
          type="button"
          className="neon-button"
          data-testid="store-confirm-submit"
          disabled={busy || !accepted}
          onClick={onConfirm}
        >
          {busy ? 'GERANDO…' : 'CONFIRMAR E GERAR PIX'}
        </button>
        <button type="button" data-testid="store-confirm-cancel" onClick={onCancel}>
          CANCELAR
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  The launch estimate                                                       */
/* -------------------------------------------------------------------------- */

/**
 * What the launch would credit, entirely as the server computed it.
 *
 * The 30% is NOT calculated here. Every figure - the eligible base, the bonus,
 * the official total - arrives already resolved, and the disclaimers travel
 * with them, because a number beside a currency word is a promise and the
 * sentences that make it not one have to be as authoritative as the number.
 *
 * Zero is a NORMAL answer, and it says why. An account whose only purchases
 * were sandbox charges should read an explanation, not an error.
 */
function RecreditSection({
  preview,
  onOpenRules,
}: {
  preview: StoreRecreditPreviewResponse | null;
  onOpenRules: () => void;
}) {
  return (
    <section className="market-body store-body" data-testid="store-recredit">
      <h3 className="store-section-title">SEU RECREDITO ESTIMADO</h3>
      {!preview ? (
        <p className="trade-empty">Carregando a estimativa…</p>
      ) : (
        <>
          {/*
            One block per reset, in the order they happen.
            The open alpha's is hidden once it is behind us AND was never
            credited to this account - a projection for a reset that already
            passed without paying anything would be a promise nobody is going
            to keep.
          */}
          {preview.projections
            .filter(
              (projection) =>
                projection.destination !== 'open_alpha' ||
                preview.stage === 'closed_alpha' ||
                projection.status === 'granted',
            )
            .map((projection) => (
              <ProjectionCard
                key={projection.destination}
                projection={projection}
                bonusPercent={preview.bonusPercent}
              />
            ))}

          <p className="store-recredit-counts" data-testid="store-recredit-counts">
            {preview.eligiblePurchaseCount} compra(s) elegível(is) · {preview.excludedPurchaseCount}{' '}
            fora do recredito
          </p>

          {preview.exclusions.length > 0 ? (
            <ul className="store-recredit-exclusions" data-testid="store-recredit-exclusions">
              {preview.exclusions.map((entry) => (
                <li key={entry.reason}>
                  {RECREDIT_EXCLUSION_LABELS[entry.reason as RecreditExclusionReason] ??
                    'Outras compras'}
                  : <strong>{entry.count}</strong>
                </li>
              ))}
            </ul>
          ) : null}

          {preview.projections.every((entry) => entry.eligiblePurchasedCash === '0') ? (
            <p className="trade-note" data-testid="store-recredit-zero">
              Você ainda não tem compras elegíveis. Pagamentos de teste (sandbox), produtos internos
              de validação, recompensas do jogo e Cash recebido de outros jogadores não contam —
              isso não é um erro.
            </p>
          ) : null}

          <p className="store-recredit-note" data-testid="store-recredit-estimate-note">
            Esta é uma estimativa baseada nas compras elegíveis atuais. Reembolsos, estornos ou
            contestações podem alterar o valor.
          </p>
          <ul className="store-recredit-disclaimers" data-testid="store-recredit-disclaimers">
            {preview.disclaimers.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <button type="button" className="store-rules-link" onClick={onOpenRules}>
            {ALPHA_TERMS_LINK_LABEL}
          </button>
        </>
      )}
    </section>
  );
}

/**
 * One reset's credit, as the server resolved it.
 *
 * Nothing is multiplied here. The base, the multiplier and the credit all
 * arrive already computed, and the card's job is to say which reset it is
 * about, whether it has happened, and - when it has - what it ACTUALLY paid,
 * which can differ from today's projection if a purchase was refunded since.
 */
function ProjectionCard({
  projection,
  bonusPercent,
}: {
  projection: StoreProjection;
  bonusPercent: number;
}) {
  const toOpenAlpha = projection.destination === 'open_alpha';
  const granted = projection.status === 'granted';
  return (
    <section
      className={`store-projection${granted ? ' is-granted' : ''}`}
      data-testid={`store-projection-${projection.destination}`}
    >
      <header className="store-projection-head">
        <strong>
          {toOpenAlpha ? 'PREVISÃO PARA O ALPHA ABERTO' : 'PREVISÃO PARA A VERSÃO OFICIAL'}
        </strong>
        <span
          className={granted ? 'store-badge' : 'store-badge is-warning'}
          data-testid={`store-projection-status-${projection.destination}`}
        >
          {granted ? 'JÁ CONCEDIDO' : 'PENDENTE'}
        </span>
      </header>
      <dl className="store-recredit-figures">
        <div>
          <dt>{toOpenAlpha ? 'Base elegível comprada' : 'Base elegível comprada nos Alphas'}</dt>
          <dd data-testid={`store-projection-base-${projection.destination}`}>
            {projection.eligiblePurchasedCash} Cash
          </dd>
        </div>
        {/* At 100% there is no bonus, and printing "+0 Cash" would read as a
            mistake rather than as the identity it is. */}
        {toOpenAlpha ? null : (
          <div>
            <dt>Bônus de lançamento ({bonusPercent}%)</dt>
            <dd data-testid={`store-projection-bonus-${projection.destination}`}>
              +{projection.bonusCash} Cash
            </dd>
          </div>
        )}
        <div className="is-total">
          <dt>Crédito previsto ({projection.multiplierBasisPoints / 100}%)</dt>
          <dd data-testid={`store-projection-credit-${projection.destination}`}>
            {projection.grantedCash} Cash
          </dd>
        </div>
      </dl>
      {granted ? (
        <p
          className="store-projection-granted"
          data-testid={`store-projection-paid-${projection.destination}`}
        >
          Concedido em {instantLabel(projection.grantedAt) ?? '—'}:{' '}
          <strong>{projection.grantedCashActual} Cash</strong>.
        </p>
      ) : null}
      <p className="store-projection-funded">
        {toOpenAlpha
          ? 'Somado a partir das compras elegíveis feitas no Alpha Fechado.'
          : 'Somado a partir das compras elegíveis feitas no Alpha Fechado e no Alpha Aberto.'}{' '}
        {projection.eligiblePurchaseCount} compra(s).
      </p>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  History                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * One receipt, read entirely from the order's FROZEN snapshot.
 *
 * The name, the benefit, the amount charged, the normal price it was discounted
 * from, the commercial phase and the accepted wording all come from the order
 * itself - never from today's catalogue and never from today's promotion. A
 * purchase made at half price still says so after the promotion ends, and one
 * made at full price never starts claiming a discount because a new promotion
 * happens to be running.
 *
 * Eligibility for the recredit is deliberately NOT shown per row: only the
 * server can decide it, it does not travel per order, and a browser guessing at
 * it would be inventing an entitlement. The aggregate estimate is the answer.
 */
function HistoryRow({
  order,
  purchasing,
  onResume,
  onRenew,
}: {
  order: StoreOrderWire;
  purchasing: boolean;
  onResume: () => void;
  onRenew: () => void;
}) {
  const presented = presentOrder(order.state);
  const phase = phaseLabel(order.gamePhase);
  const off = discountLabel(order.discountBasisPoints);
  return (
    <li data-testid={`store-order-${order.orderId}`}>
      <span className="market-row-main">
        <span className="market-row-name">
          <ProductEmblem kind={order.product.kind} />
          {order.product.name}
        </span>
        <span className="market-row-sub" data-testid={`store-order-benefit-${order.orderId}`}>
          {benefitLabel(order.product)} · <strong>{order.priceLabel}</strong>
          {off && order.normalPriceLabel ? (
            <>
              {' · '}
              <s className="store-card-was">{order.normalPriceLabel}</s>{' '}
              <span className="store-badge">{off}</span>
            </>
          ) : null}
        </span>
        <span className="market-row-sub">
          {instantLabel(order.createdAt)} · Pix · #{order.orderId.slice(0, 8)}
          {phase ? ` · ${phase}` : ''}
          {order.acceptedTermsVersion ? ` · Termos ${order.acceptedTermsVersion}` : ''}
        </span>
        {/*
          The SERVER's verdict, and only when it gave one. Null means the order
          delivered nothing yet, so there is nothing to judge - and a browser
          guessing here would be inventing an entitlement.
        */}
        {order.recreditEligible === null ? null : (
          <span
            className={order.recreditEligible ? 'store-badge' : 'store-badge is-warning'}
            data-testid={`store-order-recredit-${order.orderId}`}
          >
            {order.recreditEligible ? 'Elegível ao recredito' : 'Fora do recredito'}
          </span>
        )}
        {order.state === 'granted' ? (
          <span className="market-row-sub">
            Aprovado {instantLabel(order.approvedAt) ?? '—'} · Entregue{' '}
            {instantLabel(order.grantedAt) ?? '—'}
          </span>
        ) : null}
      </span>
      <span className={`store-state store-state-${presented.tone}`}>{presented.label}</span>
      {isLiveOrder(order.state) && order.pix ? (
        <button type="button" data-testid={`store-resume-${order.orderId}`} onClick={onResume}>
          VER PIX
        </button>
      ) : null}
      {order.state === 'expired' ? (
        <button
          type="button"
          data-testid={`store-renew-${order.orderId}`}
          disabled={!purchasing}
          onClick={onRenew}
        >
          GERAR NOVO PIX
        </button>
      ) : null}
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/*  Art                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The product's emblem, drawn as inline SVG.
 *
 * Deliberately not a raster asset: the store sells a quantity of currency and a
 * span of time, neither of which has a master file in `assets-source/`, and
 * inventing one would mean touching the asset pipeline for a shop icon. Vector
 * means transparency is preserved by construction, it scales to any resolution
 * without a second file, and it loads with the bundle rather than over the
 * network.
 *
 * `aria-hidden`: the name is right beside it, and an emblem that announced
 * itself would make a screen reader say everything twice.
 */
function ProductEmblem({ kind }: { kind: 'cash' | 'vip' }) {
  return (
    <span className={`store-emblem store-emblem-${kind}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true" role="presentation">
        {kind === 'cash' ? (
          <>
            <circle cx="12" cy="12" r="8.4" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <circle
              cx="12"
              cy="12"
              r="5.6"
              fill="none"
              stroke="currentColor"
              strokeWidth="0.9"
              opacity="0.5"
            />
            <path
              d="M12 7.6v8.8M9.8 9.6h3.4a1.7 1.7 0 0 1 0 3.4h-2.6a1.7 1.7 0 0 0 0 3.4h3.6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </>
        ) : (
          <>
            <path
              d="M4 16.5 5.6 7.4l4 3.6L12 5.5l2.4 5.5 4-3.6L20 16.5z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinejoin="round"
            />
            <path
              d="M4.6 18.6h14.8"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </>
        )}
      </svg>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  The Pix screen                                                            */
/* -------------------------------------------------------------------------- */

/**
 * What the buyer looks at while paying, and afterwards.
 *
 * The QR is an `<img>` from a server-supplied base64 payload: no canvas, no
 * generation, no filter and no animation over it. `image-rendering` is left
 * alone - a scanner reads the modules, and smoothing them is safer than forcing
 * a nearest-neighbour scale that could break at a fractional device ratio.
 */
function PaymentScreen({
  order,
  nowMs,
  copied,
  onCopy,
  onDismiss,
  onRetry,
}: {
  order: StoreOrderWire;
  nowMs: number;
  copied: boolean;
  onCopy: () => void;
  onDismiss: () => void;
  onRetry: () => void;
}) {
  const presented = presentOrder(order.state);
  const countdown = countdownLabel(order.expiresAt, nowMs);
  const payable = isLiveOrder(order.state) && order.pix?.qrCode;

  return (
    <section className="market-body store-body store-payment" data-testid="store-payment">
      <div className={`store-payment-head store-state-${presented.tone}`}>
        <span className="store-payment-product">{order.product.name}</span>
        <span className="store-payment-price">{order.priceLabel}</span>
        <span className="store-payment-state" data-testid="store-payment-state">
          {presented.label}
        </span>
      </div>
      {presented.hint ? <p className="trade-note">{presented.hint}</p> : null}

      {order.state === 'granted' ? (
        <div className="store-delivered" data-testid="store-delivered">
          <span className="store-delivered-mark" aria-hidden="true" />
          <strong>
            {order.product.kind === 'cash'
              ? `${order.product.cashAmount} Cash creditados`
              : `VIP estendido por ${durationLabel(order.product.vipSeconds)}`}
          </strong>
          <span className="market-row-sub">
            Entregue {instantLabel(order.grantedAt) ?? 'agora'}.
          </span>
        </div>
      ) : null}

      {payable ? (
        <div className="store-pix">
          {order.pix?.qrCodeBase64 ? (
            <figure className="store-qr" data-testid="store-qr">
              <img
                src={`data:image/png;base64,${order.pix.qrCodeBase64}`}
                alt={`QR Code Pix para pagar ${order.priceLabel}. O código copia e cola está abaixo.`}
                width={240}
                height={240}
              />
            </figure>
          ) : null}
          <div className="store-pix-code">
            <label htmlFor="store-pix-value">Pix Copia e Cola</label>
            <textarea
              id="store-pix-value"
              data-testid="store-pix-code"
              readOnly
              rows={3}
              value={order.pix?.qrCode ?? ''}
            />
            <button type="button" className="neon-button" data-testid="store-copy" onClick={onCopy}>
              {copied ? 'CÓDIGO COPIADO' : 'COPIAR CÓDIGO PIX'}
            </button>
            {order.pix?.ticketUrl ? (
              <a
                className="store-ticket"
                href={order.pix.ticketUrl}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="store-ticket"
              >
                ABRIR PÁGINA DE PAGAMENTO
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      {payable && countdown ? (
        <p className="store-countdown" data-testid="store-countdown">
          Expira em <strong>{countdown}</strong> · {instantLabel(order.expiresAt)}
        </p>
      ) : null}

      <div className="market-confirm-actions">
        <button type="button" data-testid="store-payment-back" onClick={onDismiss}>
          VOLTAR À LOJA
        </button>
        {order.state === 'expired' ? (
          <button
            type="button"
            className="neon-button"
            data-testid="store-payment-retry"
            onClick={onRetry}
          >
            GERAR NOVO PIX
          </button>
        ) : null}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  VIP                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * What VIP actually is, and what it is not.
 *
 * The two percentages come from the SHARED constants the server applies, so the
 * shop cannot advertise a bonus the economy does not grant. The list is
 * deliberately short: there is no gacha advantage, no better drop, no reduced
 * pity and no exclusive item, and promising any of them would be a lie the
 * codebase could not honour.
 */
function VipSummary({ catalog }: { catalog: StoreCatalogResponse | null }) {
  const vip = catalog?.vip ?? null;
  return (
    <div className="store-vip-summary" data-testid="store-vip-summary">
      <ul className="store-vip-benefits">
        <li>+{VIP_XP_BONUS_PERCENT}% de XP</li>
        <li>+{VIP_GOLD_BONUS_PERCENT}% de Gold</li>
        <li>Trade direto entre jogadores</li>
        <li>Taxa do marketplace de 16% em vez de 23%</li>
      </ul>
      <p className="store-vip-state" data-testid="store-vip-state">
        {vip?.active ? (
          <>
            VIP ativo até <strong>{instantLabel(vip.endsAt) ?? '—'}</strong> ·{' '}
            {durationLabel(vip.remainingSeconds)} restantes
          </>
        ) : (
          <>Você não tem VIP ativo.</>
        )}
      </p>
      {/* The stage's own VIP note, from the server. A purchased window is paid
          TIME, and it is the one thing a reset does not take. */}
      <p className="trade-note" data-testid="store-vip-survives">
        {catalog?.notice.vipNote ?? ''}
      </p>
    </div>
  );
}
